import React, { useState, useEffect, useMemo, SyntheticEvent } from 'react';
import { LoadScript, Autocomplete } from '@react-google-maps/api';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Link } from 'react-router-dom';
import { useRef } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Typography,
    Button,
    Chip,
    Slider,
    TextField,
    FormControlLabel,
    Checkbox,
    Rating,
    CircularProgress,
    Paper,
    Stack,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Skeleton,
    InputAdornment
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

// MUI X TreeView imports

import { useNavigate } from 'react-router-dom';
import { useLocation } from "react-router-dom";
import categoriesData from "../Categories/categories.json";
import { HandymanAppState } from '../../configuration/redux/reducers';
import { getHandymanList } from './actions';
import { BrowseHandymanQuery, HandymanViewModel } from '../../configuration/api/apiSchema';
import { connect } from 'react-redux';
import CategoryTreeSelect from '../Categories/CategoryTreeSelect/CategoryTreeSelect';
import { flattenCategories, getAllCategoryKeys } from '../Categories/helper';
import debounce from 'lodash/debounce';

/** Category data structure */
interface Category {
    key: string;
    name: string;
    subcategories: Category[];
}

/** Handyman model for typing */
interface Handyman {
    id: string;
    avatarUrl?: string;
    name: string;
    isVerified: boolean;
    registrationDate: string;   // e.g. "24 March 2019"
    lastOnline: string;         // e.g. "3 hours ago"
    rating: number;
    positiveFeedbackPercent: number; // e.g. 99
    reviewsCount: number;
    shortDescription: string;
    ratePerHour?: number;
    fixedPriceRate?: number;
    // This can include root or subcategory keys
    categories: string[];
    // NEW fields for city/district filters:
    city: string;
    cityDistrict?: string;
}



const parsedData = JSON.parse(
    JSON.stringify(categoriesData)
) as { categories: Category[] };

const mockCategoryTree = parsedData.categories;
/** Flattened dictionary for easy "key -> name" lookup */
const categoryDictionary = flattenCategories(mockCategoryTree);
/** All possible category keys */
const ALL_CATEGORY_KEYS = getAllCategoryKeys(mockCategoryTree);

/** 
 * Example cities / districts 
 * In real usage, you might want them dynamically from your DB or API.
 */
const cityDistrictMap: Record<string, string[]> = {
    'New York': ['Manhattan', 'Brooklyn'],
    'Los Angeles': ['Downtown', 'Beverly Hills'],
    'Chicago': ['Loop', 'Lincoln Park'],
};

/** Mock Handymen data */
const mockHandymen: Handyman[] = [
    {
        id: '1',
        name: 'Emily C.',
        isVerified: true,
        registrationDate: 'March 24, 2019',
        lastOnline: '3 hours ago',
        rating: 4.8,
        positiveFeedbackPercent: 99,
        reviewsCount: 63,
        shortDescription: 'I am a thorough cleaner with a strong focus on detail and hygiene.',
        avatarUrl: 'https://via.placeholder.com/48x48',
        ratePerHour: 200,
        fixedPriceRate: 1500,
        categories: ['home_services', 'plumbing_work', 'toilet_installation_repair'],
        city: 'New York',
        cityDistrict: 'Manhattan',
    },
    {
        id: '2',
        name: 'Kate B.',
        isVerified: true,
        registrationDate: 'July 13, 2016',
        lastOnline: '4 hours ago',
        rating: 4.9,
        positiveFeedbackPercent: 96,
        reviewsCount: 17,
        shortDescription: 'I offer comprehensive cleaning services for apartments and offices.',
        avatarUrl: 'https://via.placeholder.com/48x48',
        ratePerHour: 250,
        fixedPriceRate: 2000,
        categories: ['ai_services', 'ai_audio_creation', 'ai_consulting'],
        city: 'New York',
        cityDistrict: 'Brooklyn',
    },
    {
        id: '3',
        name: 'Michael H.',
        isVerified: false,
        registrationDate: 'February 10, 2022',
        lastOnline: '1 hour ago',
        rating: 4.2,
        positiveFeedbackPercent: 89,
        reviewsCount: 10,
        shortDescription: 'Reliable handyman for basic cleaning and small repairs.',
        avatarUrl: 'https://via.placeholder.com/48x48',
        ratePerHour: 180,
        fixedPriceRate: 1200,
        categories: ['event_planning', 'wedding_host', 'birthday_party_host'],
        city: 'Los Angeles',
        cityDistrict: 'Downtown',
    },
];

const mapViewModelToHandyman = (viewModelList: HandymanViewModel[]): Handyman[] => {
    return viewModelList.map((viewModel) => ({
        id: viewModel.id || "1",
        avatarUrl: viewModel.avatarUrl || "https://via.placeholder.com/48x48",
        name: viewModel.fullName || "Emily C.",
        isVerified: viewModel.isVerified || true,
        registrationDate: "March 24, 2019", // Mock data
        lastOnline: "3 hours ago", // Mock data
        rating: 4.8, // Mock data
        positiveFeedbackPercent: 99, // Mock data
        reviewsCount: 63, // Mock data
        shortDescription: viewModel.bio || "I am a thorough cleaner with a strong focus on detail and hygiene.",
        ratePerHour: viewModel.categories?.[0]?.hourlyRate || 200, // Mock data
        fixedPriceRate: viewModel.categories?.[0]?.fixedPriceRate || 1500, // Mock data
        categories: viewModel.categories?.map((category) => category.categoryKey || "home_services") || ["home_services"],
        city: viewModel.location?.split(", ")[0] || "New York", // Mock data
        cityDistrict: viewModel.location?.split(", ")[1] || "Manhattan", // Mock data
    }));
};

interface StateFromProps {
    isLoading: boolean;
    handymanList?: HandymanViewModel[];
}

interface DispatchFromProps {
    getHandymanList: (query: BrowseHandymanQuery) => void;
}

export type BrowseHandymanFilterTypes = 'categories' | 'location' | 'name' | 'price-range' | 'hourly-rate' | 'fixed-price-rate'

interface PassedProps {
    preselectedCategoryKey?: string;
    excludeFilters?: BrowseHandymanFilterTypes[];
    actionButtonTitle: string;
    onAction: (handymanId: string) => void;
    address?: string | null;
    radiusKm?: number;
}

type Props = StateFromProps & DispatchFromProps & PassedProps;

interface HandymanCardProps {
    handyman?: Handyman;
    onAction?: (id: string) => void;
    actionButtonTitle?: string;
    isSkeleton?: boolean;
}

const HandymanCard: React.FC<HandymanCardProps> = ({ handyman, onAction, actionButtonTitle, isSkeleton = false }) => {
    if (isSkeleton) {
        return (
            <Card variant="outlined" sx={{ 
                mb: 2, 
                borderRadius: 2,
                transition: 'box-shadow 0.2s',
                '&:hover': {
                    boxShadow: 3,
                },
            }}>
                <CardHeader
                    avatar={<Skeleton variant="circular" width={40} height={40} />}
                    title={
                        <Box display="flex" alignItems="center" gap={1}>
                            <Skeleton variant="text" width={120} height={32} />
                            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                        </Box>
                    }
                    subheader={<Skeleton variant="text" width={200} height={20} />}
                />
                <CardContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Skeleton variant="text" width="100%" height={20} />
                        <Skeleton variant="text" width="90%" height={20} />

                        <Box display="flex" alignItems="center" gap={2}>
                            <Skeleton variant="text" width={150} height={24} />
                            <Skeleton variant="text" width={100} height={24} />
                        </Box>

                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 16 }} />
                            <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 16 }} />
                            <Skeleton variant="rectangular" width={90} height={32} sx={{ borderRadius: 16 }} />
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Skeleton variant="text" width={120} height={24} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Box>
                            <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (!handyman || !onAction || !actionButtonTitle) {
        return null;
    }

    const categoriesList = handyman.categories.map(
        (catKey: string) => categoryDictionary[catKey] || catKey
    );

    return (
        <Card variant="outlined" sx={{ 
            mb: 2, 
            borderRadius: 2,
            transition: 'box-shadow 0.2s',
            '&:hover': {
                boxShadow: 3,
            },
        }}>
            <CardHeader
                avatar={
                    <Link to={`/browse-handyman/${handyman.id}`} style={{ textDecoration: 'none' }}>
                        <Avatar
                            src={handyman.avatarUrl}
                            alt={handyman.name}
                            sx={{ 
                                width: 40, 
                                height: 40,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: 'primary.main',
                            }}
                        />
                    </Link>
                }
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <Link to={`/browse-handyman/${handyman.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    cursor: 'pointer',
                                    color: 'inherit',
                                    '&:hover': { color: 'primary.main' },
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                }}
                            >
                                {handyman.name}
                            </Typography>
                        </Link>
                        {handyman.isVerified && (
                            <Chip 
                                label="Verified" 
                                color="success" 
                                size="small"
                                sx={{ 
                                    height: 24,
                                    '& .MuiChip-label': {
                                        px: 1,
                                        fontWeight: 500,
                                    }
                                }}
                            />
                        )}
                    </Box>
                }
                subheader={
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Registered: {handyman.registrationDate} • Last Online: {handyman.lastOnline}
                    </Typography>
                }
            />
            <CardContent>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="body1" color="text.primary">
                        {handyman.shortDescription}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Rating
                                name={`rating-${handyman.id}`}
                                value={handyman.rating}
                                precision={0.1}
                                readOnly
                                size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                                ({handyman.rating})
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {handyman.reviewsCount} reviews • {handyman.positiveFeedbackPercent}% positive
                        </Typography>
                    </Box>

                    <Box display="flex" gap={1} flexWrap="wrap">
                        {categoriesList.map((category: string, index: number) => (
                            <Chip
                                key={index}
                                label={category}
                                size="small"
                                sx={{
                                    borderRadius: 4,
                                    bgcolor: 'action.hover',
                                    color: 'text.primary',
                                    '&:hover': { bgcolor: 'action.selected' },
                                }}
                            />
                        ))}
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                        <Box>
                            {handyman.ratePerHour && (
                                <Typography variant="body2" color="text.secondary">
                                    Hourly Rate: <Box component="span" color="primary.main" fontWeight="600">${handyman.ratePerHour}</Box>
                                </Typography>
                            )}
                            {handyman.fixedPriceRate && (
                                <Typography variant="body2" color="text.secondary">
                                    Fixed Price: <Box component="span" color="primary.main" fontWeight="600">${handyman.fixedPriceRate}</Box>
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            onClick={() => onAction(handyman.id)}
                            sx={{ 
                                fontWeight: 500, 
                                textTransform: 'none',
                                minWidth: 120,
                                boxShadow: 2,
                                '&:hover': {
                                    boxShadow: 4,
                                }
                            }}
                        >
                            {actionButtonTitle}
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const BrowseHandymanList = (props: Props) => {
    const location = useLocation();
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

    // Category selection: store a set of checked category keys
    const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<Set<string>>(new Set());

    // We use string for search fields, numeric for sliders, etc.

    const [handymanSearchText, setHandymanSearchText] = useState<string>('');
    const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
    const [onlyVerified, setOnlyVerified] = useState<boolean>(false);
    const [hourlyRateRange, setHourlyRateRange] = useState<[number, number]>([0, 1000]);
    const [fixedPriceRange, setFixedPriceRange] = useState<[number, number]>([0, 10000]);

    // NEW: City + District states
    const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
    const [radiusKm, setRadiusKm] = useState<number | null>(null);
    const [appliedRadiusKm, setAppliedRadiusKm] = useState<number | null>(null); // triggers fetch

    // Simulate loading from an API
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const debouncedFetchHandymen = useMemo(
        () => debounce(() => {
          fetchHandymen();
        }, 3000), // adjust delay as needed
        [selectedAddress, appliedRadiusKm, selectedCategories, searchTerm, priceRange, hourlyRateRange, fixedPriceRange, onlyVerified, page]
      );

    useEffect(() => {
        // Initial load
        debouncedFetchHandymen();
    }, []);

    useEffect(() => {
        debouncedFetchHandymen();
      }, [selectedCategories, searchTerm, priceRange, hourlyRateRange, fixedPriceRange, onlyVerified, selectedAddress, appliedRadiusKm, page]);         

    const fetchHandymen = (radiusOverride?: number) => {
        const query: BrowseHandymanQuery = {
            address: selectedAddress || null,
            radiusKm: radiusOverride ?? appliedRadiusKm ?? null,
            /* categoryKeys: selectedCategories.length > 0 ? selectedCategories : null,
            city: selectedCity || null,
            cityDistrict: selectedDistrict || null,
            searchTerm: searchTerm || null,
            minPriceRange: priceRange[0],
            maxPriceRange: priceRange[1],
            minHourlyRate: hourlyRateRange[0],
            maxHourlyRate: hourlyRateRange[1],
            minFixedPrice: fixedPriceRange[0],
            maxFixedPrice: fixedPriceRange[1],
            verifiedOnly: onlyVerified,
            page: page,
            pageSize: pageSize */
        };
        props.getHandymanList(query);
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    useEffect(() => {
        // Load categories from JSON on mount
        const parsedData = JSON.parse(
            JSON.stringify(categoriesData)
        ) as { categories: Category[] };
        setCategories(parsedData.categories);
    }, []);



    const navigate = useNavigate();

    // Parse query parameters on page load
    useEffect(() => {
        if (location.search && !props.preselectedCategoryKey) {
            const queryParams = new URLSearchParams(location.search);

            // Get selected categories
            const categories = queryParams.get("selectedCategories");
            if (categories) {
                setSelectedCategoryKeys(new Set(categories.split(",")));
            }

            const address = queryParams.get("address");
            if (address) {
                setSelectedAddress(address);
            }
        }

        if (props.preselectedCategoryKey) {
            setSelectedCategoryKeys(new Set([props.preselectedCategoryKey]))
        }

    }, []);

    // On mount / whenever selected categories change, load or filter data
    useEffect(() => {
        if (!props.preselectedCategoryKey) {
            setIsLoading(true);
            const timer = setTimeout(() => {
                const selectedKeysArray = Array.from(selectedCategoryKeys);
                const joinedKeys = selectedKeysArray.join(',');

                // For example: /browse-handyman?selectedCategories=home-cleaning,move-in-out-cleaning
                navigate(`/browse-handyman?selectedCategories=${encodeURIComponent(joinedKeys)}`);

                setIsLoading(false);
            }, 1000);

            return () => clearTimeout(timer);
        }

    }, [selectedCategoryKeys]);

    /** Toggle a single category's checked state */
    const toggleCategory = (catKey: string) => {
        setSelectedCategoryKeys((prev) => {
            const updated = new Set(prev);
            if (updated.has(catKey)) {
                updated.delete(catKey);
            } else {
                updated.add(catKey);
            }
            return updated;
        });
    };

    /** "Select All" means check every category key. */
    const handleSelectAllCategories = () => {
        setSelectedCategoryKeys(new Set(ALL_CATEGORY_KEYS));
    };

    const handleDeselectAllCategories = () => {
        setSelectedCategoryKeys(new Set());
    };

    /** "Reset Filters" clears everything. */
    const handleResetFilters = () => {
        setSelectedCategoryKeys(new Set());
        setHandymanSearchText('');
        setPriceRange([0, 10000]);
        setOnlyVerified(false);
        setHourlyRateRange([0, 1000]);
        setFixedPriceRange([0, 10000]);
    };

    // Handlers for the other filters
    const handleHandymanSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handlePriceRangeChange = (event: Event, newValue: number | number[]) => {
        setPriceRange(newValue as [number, number]);
    };

    const handleHourlyRateRangeChange = (event: Event, newValue: number | number[]) => {
        setHourlyRateRange(newValue as [number, number]);
    };

    const handleFixedPriceRangeChange = (event: Event, newValue: number | number[]) => {
        setFixedPriceRange(newValue as [number, number]);
    };

    /** 
     * The final filter for handymen:
     * - If selectedCategoryKeys is empty => show all.
     * - Otherwise, must have intersection with the selectedCategoryKeys.
     * - Then filter by name, verified, city/district, price, etc.
     */
    const filteredHandymen = mapViewModelToHandyman(props.handymanList ?? []);

    const isCategoriesFilterVisible = !props.excludeFilters?.includes('categories');
    const isLocationFilterVisible = !props.excludeFilters?.includes('location');
    const isNameFilterVisible = !props.excludeFilters?.includes('name');
    const isPriceRangeFilterVisible = !props.excludeFilters?.includes('price-range');


    return (<>
        <Grid container spacing={3}>
            {/* Left sidebar for Category Tree & filters */}
            <Grid item xs={12} md={3}>
                <Card sx={{ 
                    borderRadius: 2,
                    position: 'sticky',
                    top: 24,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Filters
                        </Typography>

                        {isCategoriesFilterVisible && (
                            <Box mb={3}>
                                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleResetFilters}
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Reset Filters
                                    </Button>
                                </Stack>

                                <CategoryTreeSelect
                                    selectedCategoryKeys={selectedCategoryKeys}
                                    toggleCategory={toggleCategory}
                                    isSingleSelect={false}
                                    categories={mockCategoryTree}
                                    isDisabledCheckboxes={props.preselectedCategoryKey !== undefined}
                                />

                                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ textTransform: 'none' }}
                                        onClick={handleSelectAllCategories}
                                    >
                                        Select All
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ textTransform: 'none' }}
                                        onClick={handleDeselectAllCategories}
                                    >
                                        Deselect All
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        {isLocationFilterVisible && (
                            <Box mb={3}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Location
                                </Typography>

                                <Autocomplete
                                onLoad={(autocomplete) => {
                                    autocompleteRef.current = autocomplete;
                                }}
                                onPlaceChanged={() => {
                                    const place = autocompleteRef.current?.getPlace();
                                    if (place?.formatted_address) {
                                    setSelectedAddress(place.formatted_address);
                                    }
                                }}
                                >
                                <TextField
                                    label="Search Address"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                />
                                </Autocomplete>

                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 0.5 }}>
                                    Search Radius (km)
                                </Typography>
                                <Slider
                                    value={radiusKm ?? 0}
                                    onChange={(e, value) => setRadiusKm(value as number)}
                                    onChangeCommitted={(e, value) => {
                                        setAppliedRadiusKm(value as number); // update state
                                      }}
                                    min={0}
                                    max={20}
                                    step={0.5}
                                    valueLabelDisplay="auto"
                                />
                            </Box>
)}


                        {isNameFilterVisible && (
                            <Box mb={3}>
                                <TextField
                                    fullWidth
                                    label="Search handymen"
                                    variant="outlined"
                                    size="small"
                                    value={handymanSearchText}
                                    onChange={handleHandymanSearchChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        )}

                        {isPriceRangeFilterVisible && (
                            <Box mb={3}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Price Ranges
                                </Typography>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Hourly Rate ($)
                                </Typography>
                                <Slider
                                    value={hourlyRateRange}
                                    onChange={handleHourlyRateRangeChange}
                                    min={0}
                                    max={1000}
                                    valueLabelDisplay="auto"
                                    sx={{ mb: 3 }}
                                />

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Fixed Price ($)
                                </Typography>
                                <Slider
                                    value={fixedPriceRange}
                                    onChange={handleFixedPriceRangeChange}
                                    min={0}
                                    max={10000}
                                    valueLabelDisplay="auto"
                                />
                            </Box>
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyVerified}
                                    onChange={(e) => setOnlyVerified(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Verified Only
                                </Typography>
                            }
                        />
                    </CardContent>
                </Card>
            </Grid>

            {/* Main content area */}
            <Grid item xs={12} md={9}>
                {props.isLoading ? (
                    <Box>
                        {[1, 2, 3, 4, 5, 6].map((index) => (
                            <HandymanCard
                                key={index}
                                isSkeleton={true}
                                handyman={undefined}
                                onAction={undefined}
                                actionButtonTitle={undefined}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box>
                        {filteredHandymen.map((handyman) => (
                            <HandymanCard
                                key={handyman.id}
                                handyman={handyman}
                                onAction={props.onAction}
                                actionButtonTitle={props.actionButtonTitle}
                            />
                        ))}
                    </Box>
                )}
            </Grid>
        </Grid>
    </>);
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
    isLoading: state.handyman.isLoading,
    handymanList: state.handyman.handymanList,
});

export default connect<StateFromProps, DispatchFromProps, PassedProps, HandymanAppState>(
    mapStateToProps,
    {
        getHandymanList,
    }
)(BrowseHandymanList);
